class PiCaseContent extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    this.controller = new AbortController();
    this.contentElement = this.querySelector('[data-pi2-selected-case-page-content]');
    this.caseTypesElement = this.querySelector('[data-pi2-case-types]');
    this.currentTemplate = this.querySelector('template[data-pi2-current-case-page-template]');
    if (!this.contentElement || !this.caseTypesElement || !this.currentTemplate) return;

    this.caseTypeLookup = new Map();
    this.contentCache = new Map();
    this.pendingCaseType = String(this.dataset.pi2CurrentCase || '').trim();
    this.pendingVariantId = String(this.dataset.pi2InitialVariantId || '').trim();
    this.hasEnteredViewport = false;
    this.renderToken = 0;

    this.#registerCaseTypes();
    if (this.pendingCaseType) {
      this.contentCache.set(this.#normalize(this.pendingCaseType), this.currentTemplate.innerHTML);
    }

    document.addEventListener('pi:variant-changed', this.#handleVariantChange, {
      signal: this.controller.signal,
    });

    if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
      this.intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
        rootMargin: '700px 0px',
      });
      this.intersectionObserver.observe(this);
    } else {
      this.#activate();
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.fetchController?.abort();
    this.intersectionObserver?.disconnect();
    this.controller = null;
  }

  #normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  #compact(value) {
    return this.#normalize(value).replace(/[\s\-_/]/g, '');
  }

  #registerCaseTypes() {
    let caseTypes = [];

    try {
      caseTypes = JSON.parse(this.caseTypesElement.textContent || '[]');
    } catch (_error) {
      caseTypes = [];
    }

    caseTypes.forEach((value) => {
      const caseType = String(value || '').trim();
      if (!caseType) return;

      this.caseTypeLookup.set(caseType, caseType);
      this.caseTypeLookup.set(this.#normalize(caseType), caseType);
      this.caseTypeLookup.set(this.#compact(caseType), caseType);
    });
  }

  #resolveCaseType(variant) {
    if (!variant || !Array.isArray(variant.options)) return '';

    for (const optionValue of variant.options) {
      const raw = String(optionValue || '').trim();
      const match =
        this.caseTypeLookup.get(raw) ||
        this.caseTypeLookup.get(this.#normalize(raw)) ||
        this.caseTypeLookup.get(this.#compact(raw));

      if (match) return match;
    }

    return '';
  }

  #handleIntersection = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.intersectionObserver?.disconnect();
    this.#activate();
  };

  #activate() {
    if (this.hasEnteredViewport) return;
    this.hasEnteredViewport = true;
    this.#renderCasePage(this.pendingCaseType, this.pendingVariantId);
  }

  #handleVariantChange = (event) => {
    const variant = event.detail?.variant;
    const caseType = this.#resolveCaseType(variant);
    if (!caseType) return;

    this.pendingCaseType = caseType;
    this.pendingVariantId = String(variant.id || '').trim();

    if (this.hasEnteredViewport) {
      this.#renderCasePage(caseType, this.pendingVariantId);
    }
  };

  async #renderCasePage(caseType, variantId) {
    const normalizedCaseType = this.#normalize(caseType);
    if (!normalizedCaseType) {
      this.contentElement.setAttribute('aria-busy', 'false');
      return;
    }

    const token = ++this.renderToken;
    this.contentElement.setAttribute('aria-busy', 'true');

    let html = this.contentCache.get(normalizedCaseType);
    if (html == null && variantId) {
      html = await this.#fetchCasePage(variantId);
      if (html != null) this.contentCache.set(normalizedCaseType, html);
    }

    if (token !== this.renderToken || html == null) {
      if (token === this.renderToken) this.contentElement.setAttribute('aria-busy', 'false');
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    const fragment = template.content;

    fragment.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.decoding = 'async';
      image.fetchPriority = 'low';
      if (!image.hasAttribute('alt')) image.alt = '';
    });

    fragment.querySelectorAll('iframe').forEach((frame) => {
      frame.loading = 'lazy';
    });

    this.contentElement.replaceChildren(fragment);
    this.contentElement.querySelectorAll('rte-formatter table').forEach((table) => {
      if (table.parentElement?.classList.contains('rte-table-wrapper')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'rte-table-wrapper';
      table.before(wrapper);
      wrapper.appendChild(table);
    });

    this.contentElement.setAttribute('aria-busy', 'false');
  }

  async #fetchCasePage(variantId) {
    this.fetchController?.abort();
    this.fetchController = new AbortController();

    const url = new URL(this.dataset.pi2ProductUrl || window.location.pathname, window.location.origin);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', this.dataset.pi2SectionId || '');

    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.fetchController.signal,
      });
      if (!response.ok) return null;

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const matchingRoot = Array.from(
        documentFragment.querySelectorAll('[data-pi2-selected-case-page]')
      ).find((element) => element.dataset.pi2BlockId === this.dataset.pi2BlockId);
      const template = matchingRoot?.querySelector('template[data-pi2-current-case-page-template]');

      return template?.innerHTML ?? null;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('Unable to load case feature content.', error);
      }
      return null;
    }
  }
}

if (!customElements.get('pi-case-content')) {
  customElements.define('pi-case-content', PiCaseContent);
}
